import { useState } from "react"
import { useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { TrashSimple } from "@phosphor-icons/react"
import { formatDate } from "date-fns"
import { useUser } from "@/apis/bff"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Button } from "@/components/ui/button"
import { DrawerFooter } from "@/components/ui/drawer"
import { UserProvider } from "@/components/user-wrapper"
import { TimeWindowDeleteDialog } from "@/modules/patients/agenda/time-window/components/time-window-delete-dialog"
import { TimeWindowRestrictedDialog } from "@/modules/patients/agenda/time-window/components/time-window-restricted-dialog"
import { TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"
import { DELETE_RESTRICTION_TIMEOUT } from "@/modules/patients/constants/Agenda"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { isTimeWindowDeletionRestricted } from "@/modules/patients/libs/time-window-deletion-restriction"
import { useSwissTimeWindowDeleteMutation } from "@/modules/patients/services/ch/useChDeleteTimeWindow"

interface TimeWindowFooterProps {
  timeWindow?: TimeWindowFormSchema
  isViewMode: boolean
  isEditMode: boolean
  onSave: () => void
  onClose: () => void
}

const LastUpdatedInfo = ({ at, by }: { at?: Date; by?: string }) => {
  const { t } = useTranslation()

  return (
    <div className="text-on-surface-subdued mr-auto flex flex-row items-center gap-2 text-xs text-ellipsis">
      {by && (
        <UserProvider
          userName={by}
          returnMissingUser={false}
          render={(updatedByUser) => (
            <UserAvatarTooltip user={updatedByUser} uniqueIdentifier="last-updated-atoss-file" />
          )}
        />
      )}
      {at && (
        <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
          {`${t("patient.agenda.timeWindowForm.updatedOn")} ${formatDate(new Date(at), "dd.MM.yyyy HH:mm")}`}
        </div>
      )}
    </div>
  )
}

export const TimeWindowFooter = ({ timeWindow, isViewMode, isEditMode, onSave, onClose }: TimeWindowFooterProps) => {
  const { t, i18n } = useTranslation()
  const patientId = usePatientParam()
  const user = useUser()

  const { errors } = useFormState<TimeWindowFormSchema>()
  const canSave = Object.keys(errors).length === 0

  const { deleteTimeWindow } = useSwissTimeWindowDeleteMutation(patientId, i18n.language)

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [restrictionDialogOpen, setRestrictionDialogOpen] = useState(false)

  const deletionRestrictionParams = timeWindow?.createdAt
    ? {
        limit: DELETE_RESTRICTION_TIMEOUT,
        isCustomerServiceCenter: user.role === "CustomerServiceCenter",
        createdAt: new Date(timeWindow.createdAt),
      }
    : undefined

  const canDelete =
    isEditMode &&
    !!timeWindow?.id &&
    !!deletionRestrictionParams &&
    !isTimeWindowDeletionRestricted(deletionRestrictionParams)

  const lastUpdatedAt = timeWindow?.updatedAt || timeWindow?.createdAt
  const lastUpdatedBy = timeWindow?.updatedBy || timeWindow?.createdBy

  const handleDeleteClick = () => {
    if (!deletionRestrictionParams) return
    if (isTimeWindowDeletionRestricted(deletionRestrictionParams)) setRestrictionDialogOpen(true)
    else setConfirmDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!timeWindow?.id || !deletionRestrictionParams) return

    setConfirmDialogOpen(false)
    if (isTimeWindowDeletionRestricted(deletionRestrictionParams)) {
      setRestrictionDialogOpen(true)
      return
    }

    deleteTimeWindow({ id: timeWindow.id })
    onClose()
  }

  return (
    <>
      <DrawerFooter className="bg-surface sticky inset-x-0 bottom-0 flex justify-end gap-2">
        <LastUpdatedInfo at={lastUpdatedAt} by={lastUpdatedBy} />
        {!isViewMode && (
          <>
            {canDelete && (
              <Button
                className="border-none"
                onClick={handleDeleteClick}
                variant="secondary"
                data-testid="time-window-drawer-delete-btn">
                <TrashSimple />
              </Button>
            )}
            <Button onClick={onClose} variant="secondary" data-testid="time-window-drawer-cancel-btn">
              {t("common.button.cancel")}
            </Button>
            <Button onClick={onSave} disabled={!canSave} data-testid="time-window-drawer-save-btn">
              {t("common.button.save")}
            </Button>
          </>
        )}
      </DrawerFooter>
      <TimeWindowRestrictedDialog
        open={restrictionDialogOpen}
        onConfirm={(event) => {
          event.stopPropagation()
          setRestrictionDialogOpen(false)
        }}
      />
      <TimeWindowDeleteDialog
        open={confirmDialogOpen}
        onDiscard={handleConfirmDelete}
        onConfirm={() => setConfirmDialogOpen(false)}
      />
    </>
  )
}
